import React, { FC } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { APP_ROUTES } from '@/shared/config/routes-config';
import { UserRole } from '@/shared/enums/user-role.enum';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import DefaultLayout from '@/shared/layouts/default/default.layout';

const LeaveRequestsListContainer = React.lazy(
  () => import('../../containers/leave-requests-list/leave-requests-list.container')
);

const LeaveDashboardContainer = React.lazy(
  () => import('../../containers/leave-dashboard/leave-dashboard.container')
);

const TeamLeaveRequestsContainer = React.lazy(
  () => import('../../containers/team-leave-requests/team-leave-requests.container')
);

const TeamLeaveDashboardContainer = React.lazy(
  () => import('../../containers/team-leave-dashboard/team-leave-dashboard.container')
);

/**
 * The HR / Leave module is visible to every internal admin role — every
 * employee should be able to file and track their own leave. Horilla
 * enforces "own data only" on the downstream API as a second layer of
 * defense.
 */
const HR_PERMITTED_ROLES = [
  UserRole.SUPER_ADMIN,
  UserRole.SALES_AGENT,
  UserRole.SALES_MANAGER,
  UserRole.SALES_QUALITY_MANAGER,
  UserRole.SALES_QUALITY_AGENT,
  UserRole.MARKETING_EXECUTIVE,
  UserRole.FINANCE_OFFICER,
  UserRole.E_COMMERCE_AUTO,
  UserRole.E_COMMERCE_FINANCE,
  UserRole.E_COMMERCE_SERVICE_CENTER,
  UserRole.E_COMMERCE_REFERRAL,
  UserRole.E_COMMERCE_MANAGER,
  UserRole.OPERATION_MANAGER,
  UserRole.OPERATION_AGENT,
  UserRole.ADMIN,
  UserRole.HR_REPRESENTATIVE,
  UserRole.HR_MANAGER
];

const createProtectedRoute = (component: FC, addLayout = false): React.ReactElement => (
  <React.Suspense fallback={<div>Loading...</div>}>
    <DefaultLayout
      {...(addLayout && { addLayout })}
      authenticationRequired={true}
      permittedList={HR_PERMITTED_ROLES}
      component={component}
    />
  </React.Suspense>
);

const ROUTES = APP_ROUTES.HR_MODULE.CHILDREN as RouteConfig;

export const HrRoutesConfig: RouteObject[] = [
  {
    index: true,
    element: <Navigate to={ROUTES.LEAVE_REQUESTS_LIST.FULL_PATH} />
  },
  {
    path: ROUTES.LEAVE_REQUESTS_LIST.FULL_PATH,
    element: createProtectedRoute(LeaveRequestsListContainer)
  },
  {
    path: ROUTES.LEAVE_REQUEST_DETAILS.FULL_PATH,
    element: createProtectedRoute(LeaveRequestsListContainer)
  },
  {
    path: ROUTES.LEAVE_DASHBOARD.FULL_PATH,
    element: createProtectedRoute(LeaveDashboardContainer)
  },
  {
    path: ROUTES.TEAM_LEAVE_REQUESTS.FULL_PATH,
    element: createProtectedRoute(TeamLeaveRequestsContainer)
  },
  {
    path: ROUTES.TEAM_LEAVE_DASHBOARD.FULL_PATH,
    element: createProtectedRoute(TeamLeaveDashboardContainer)
  }
];
